import { MonitorStatus } from './monitor-status';

export const monitorStatusBgCssClass: Record<MonitorStatus, string> = {
    [MonitorStatus.null]: '',
    [MonitorStatus.clear]: '',
    [MonitorStatus.info]: 'monstat-bg-info',
    [MonitorStatus.watch]: 'monstat-bg-watch',
    [MonitorStatus.warning]: 'monstat-bg-warning',
    [MonitorStatus.critical]: 'monstat-bg-critical',
    [MonitorStatus.emergency]: 'monstat-bg-emergency'
};

export const monStatBadgeColors: Record<MonitorStatus, string> = {
    [MonitorStatus.null]: '',
    [MonitorStatus.clear]: '',
    [MonitorStatus.info]: 'monstat-badge-info',
    [MonitorStatus.watch]: 'monstat-badge-watch',
    [MonitorStatus.warning]: 'monstat-badge-warning',
    [MonitorStatus.critical]: 'monstat-badge-critical',
    [MonitorStatus.emergency]: 'monstat-badge-emergency'
};

export const monitorStatusTextCssClass: Record<MonitorStatus, string> = {
    [MonitorStatus.null]: '',
    [MonitorStatus.clear]: '',
    [MonitorStatus.info]: 'monstat-txt-info',
    [MonitorStatus.watch]: 'monstat-txt-watch',
    [MonitorStatus.warning]: 'monstat-txt-warning',
    [MonitorStatus.critical]: 'monstat-txt-critical',
    [MonitorStatus.emergency]: 'monstat-txt-emergency'
};

const alpha = 60;
let fillColors: Record<MonitorStatus, [number, number, number, number]> | null = null;
let outlineColors: Record<MonitorStatus, [number, number, number, number]> | null = null;
let fillColorsHex: Record<MonitorStatus, string> | null = null;
let outlineColorsHex: Record<MonitorStatus, string> | null = null;

const defaultColors: Record<keyof typeof MonitorStatus, [number, number, number, number]> = {
    null: [0, 0, 0, 0],
    clear: [255, 255, 255, alpha],
    info: [255, 255, 128, alpha],
    watch: [255, 255, 0, alpha],
    warning: [255, 191, 0, alpha],
    critical: [255, 112, 112, alpha],
    emergency: [255, 32, 223, alpha]
};

const defaultOutlineColors: Record<keyof typeof MonitorStatus, [number, number, number, number]> = {
    null: [0, 0, 0, 0],
    clear: [255, 255, 255, 255],
    info: [255, 255, 128, 255],
    watch: [255, 255, 0, 255],
    warning: [255, 191, 0, 255],
    critical: [255, 112, 112, 255],
    emergency: [255, 32, 223, 255]
};

function readColorsFromCss(): Record<MonitorStatus, string> {
    const tempElement = document.createElement('div');
    tempElement.style.display = 'none';
    document.body.appendChild(tempElement);

    const colors: Record<string, string> = {};
    for (const [status, className] of Object.entries(monitorStatusBgCssClass)) {
        tempElement.className = className;
        const style = getComputedStyle(tempElement);
        colors[status] = style.backgroundColor || 'rgba(0, 0, 0, 0)';
    }

    document.body.removeChild(tempElement);
    return colors as Record<MonitorStatus, string>;
}

function parseColorToRgba(colorString: string, alpha: number): [number, number, number, number] {
    const match = colorString.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([0-9.]+))?\)/);
    if (match) {
        return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3]), alpha];
    }
    return [0, 0, 0, 0];
}

function colorToHex(color: [number, number, number, number]): string {
    const [r, g, b, a] = color;
    return `rgba(${r}, ${g}, ${b}, ${a / 255})`;
}

function initializeColors(): void {
    if (fillColors) {
        return;
    }

    const cssColorsRaw = readColorsFromCss();

    // Convert CSS colors to RGBA arrays with alpha
    fillColors = {} as Record<MonitorStatus, [number, number, number, number]>;
    outlineColors = {} as Record<MonitorStatus, [number, number, number, number]>;
    fillColorsHex = {} as Record<MonitorStatus, string>;
    outlineColorsHex = {} as Record<MonitorStatus, string>;

    for (const [statusKey, colorString] of Object.entries(cssColorsRaw)) {
        const status = MonitorStatus[statusKey as keyof typeof MonitorStatus];
        if (status !== undefined) {
            // Parse color and apply alpha
            const colorWithAlpha = colorString === 'rgba(0, 0, 0, 0)' ? [0, 0, 0, 0] : parseColorToRgba(colorString, alpha);
            const outlineColorWithAlpha = parseColorToRgba(colorString, 255);

            fillColors[status] = colorWithAlpha as [number, number, number, number];
            outlineColors[status] = outlineColorWithAlpha as [number, number, number, number];

            // Convert to hex format
            fillColorsHex[status] = colorToHex(colorWithAlpha as [number, number, number, number]);
            outlineColorsHex[status] = colorToHex(outlineColorWithAlpha as [number, number, number, number]);
        }
    }

    // Fallback to default colors if CSS reading fails
    for (const [statusKey, defaultColor] of Object.entries(defaultColors)) {
        const status = MonitorStatus[statusKey as keyof typeof MonitorStatus];
        if (status !== undefined && !fillColors[status]) {
            fillColors[status] = defaultColor;
            outlineColors[status] = defaultOutlineColors[statusKey as keyof typeof defaultOutlineColors];
            fillColorsHex[status] = colorToHex(defaultColor);
            outlineColorsHex[status] = colorToHex(defaultOutlineColors[statusKey as keyof typeof defaultOutlineColors]);
        }
    }
}

export function getMonitorStatusColor(status: MonitorStatus): [number, number, number, number] {
    initializeColors();
    return fillColors![MonitorStatus[status] as keyof typeof fillColors] || [0, 0, 0, 0];
}

export function getMonitorStatusOutlineColor(status: MonitorStatus): [number, number, number, number] {
    initializeColors();
    return outlineColors![MonitorStatus[status] as keyof typeof outlineColors] || [0, 0, 0, 0];
}

export function getMonitorStatusColorHex(status: MonitorStatus): string {
    initializeColors();
    return fillColorsHex![MonitorStatus[status] as keyof typeof fillColorsHex] || '#00000000';
}

export function getMonitorStatusOutlineColorHex(status: MonitorStatus): string {
    initializeColors();
    return outlineColorsHex![MonitorStatus[status] as keyof typeof outlineColorsHex] || '#00000000';
}

export default {
    getMonitorStatusColor,
    getMonitorStatusOutlineColor,
    getMonitorStatusColorHex,
    getMonitorStatusOutlineColorHex,
    cssColors: monitorStatusBgCssClass
};
